import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import { dashboardService } from '../../services/dashboardService';
import * as XLSX from 'xlsx';
import {jsPDF} from 'jspdf';
import {autoTable} from 'jspdf-autotable';

const TIPO_OS_COLORS = [
  { color: '#8b5cf6', bgColor: '#ede9fe', textColor: '#5b21b6' },
  { color: '#06b6d4', bgColor: '#cffafe', textColor: '#0e7490' },
  { color: '#f97316', bgColor: '#ffedd5', textColor: '#c2410c' },
  { color: '#84cc16', bgColor: '#ecfccb', textColor: '#4d7c0f' },
  { color: '#ec4899', bgColor: '#fce7f3', textColor: '#be185d' },
  { color: '#14b8a6', bgColor: '#ccfbf1', textColor: '#0f766e' },
  { color: '#f59e0b', bgColor: '#fef3c7', textColor: '#92400e' },
  { color: '#3b82f6', bgColor: '#dbeafe', textColor: '#1e40af' },
  { color: '#ef4444', bgColor: '#fee2e2', textColor: '#991b1b' },
  { color: '#10b981', bgColor: '#d1fae5', textColor: '#065f46' },
  { color: '#6366f1', bgColor: '#e0e7ff', textColor: '#3730a3' },
  { color: '#e11d48', bgColor: '#ffe4e6', textColor: '#9f1239' },
  { color: '#0891b2', bgColor: '#cffafe', textColor: '#155e75' },
  { color: '#ca8a04', bgColor: '#fef9c3', textColor: '#854d0e' },
  { color: '#7c3aed', bgColor: '#ede9fe', textColor: '#4c1d95' },
  { color: '#059669', bgColor: '#d1fae5', textColor: '#064e3b' },
  { color: '#d946ef', bgColor: '#fae8ff', textColor: '#86198f' },
  { color: '#0284c7', bgColor: '#e0f2fe', textColor: '#075985' },
  { color: '#dc2626', bgColor: '#fee2e2', textColor: '#7f1d1d' },
  { color: '#65a30d', bgColor: '#ecfccb', textColor: '#365314' },
];

const ServiceOrders = () => {
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [allOrdersForChart, setAllOrdersForChart] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [chartDataRevenue, setChartDataRevenue] = useState([]);
  const [tiposOSList, setTiposOSList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncDates, setSyncDates] = useState({
    startDate: '',
    endDate: ''
  });

  const [showExportMenu, setShowExportMenu] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [technicianChartMode, setTechnicianChartMode] = useState('quantity');
  const [equipmentList, setEquipmentList] = useState([]);
  const [equipmentSearch, setEquipmentSearch] = useState('');
  
  const [sortConfig, setSortConfig] = useState({
    key: 'open_date',
    direction: 'desc'
  });
  
  const [filters, setFilters] = useState({
    startDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    status: [],
    equipment: [],
    technician: '',
    type: [],
    search: ''
  });

  const getStatusColor = (status) => {
    const statusLower = status.toLowerCase();
    switch(statusLower) {
      case 'pendente':
        return { bgColor: '#fef3c7', textColor: '#92400e' };
      case 'aberto':
        return { bgColor: '#dbeafe', textColor: '#1e40af' };
      case 'cancelado':
        return { bgColor: '#fee2e2', textColor: '#991b1b' };
      case 'faturado':
        return { bgColor: '#d1fae5', textColor: '#065f46' };
      default:
        return { bgColor: '#f3f4f6', textColor: '#374151' };
    }
  };

  const formatCurrency = (value) => {
    if (value >= 1000000) {
      return `R$ ${(value / 1000000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}M`;
    } else if (value >= 1000) {
      return `R$ ${(value / 1000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}k`;
    }
    return `R$ ${parseFloat(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatCurrencyFull = (value) => {
    return `R$ ${parseFloat(value || 0).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const handleSort = (key) => {
    let direction = 'asc';

    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    setSortConfig({ key, direction });
    setCurrentPage(1);
    loadData(1, { key, direction });
  };

  const getSortedOrders = () => {
    return orders; 
  };

  const SortableHeader = ({ label, sortKey, style }) => (
    <th 
      onClick={() => handleSort(sortKey)}
      style={{ 
        ...style,
        cursor: 'pointer',
        userSelect: 'none',
        position: 'relative',
        transition: 'background-color 0.2s ease'
      }}
      onMouseOver={(e) => e.target.style.backgroundColor = '#f1f5f9'}
      onMouseOut={(e) => e.target.style.backgroundColor = '#f8fafc'}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        {label}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
          <svg 
            width="8" 
            height="4" 
            viewBox="0 0 8 4" 
            style={{ 
              fill: sortConfig.key === sortKey && sortConfig.direction === 'asc' ? '#3b82f6' : '#d1d5db' 
            }}
          >
            <path d="M0 4L4 0L8 4H0Z"/>
          </svg>
          <svg 
            width="8" 
            height="4" 
            viewBox="0 0 8 4" 
            style={{ 
              fill: sortConfig.key === sortKey && sortConfig.direction === 'desc' ? '#3b82f6' : '#d1d5db' 
            }}
          >
            <path d="M0 0L4 4L8 0H0Z"/>
          </svg>
        </div>
      </div>
    </th>
  );

  const processChartData = (ordersData) => {
    if (!ordersData || ordersData.length === 0) {
      setChartData([]);
      setChartDataRevenue([]);
      return;
    }

    const monthlyData = {};
    const tiposSet = new Set();

    ordersData.forEach((order) => {
      const dateStr = order.open_date;
      const [year, month, day] = dateStr.split('-');
      const monthKey = `${year}-${month}`;

      const date = new Date(year, parseInt(month) - 1, parseInt(day));
      const monthName = date.toLocaleDateString('pt-BR', {
        year: 'numeric',
        month: 'short'
      }).replace(' de ', '/');

      const tipoOS = order.type || 'Sem Tipo';
      tiposSet.add(tipoOS);

      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { month: monthName };
      }

      if (!monthlyData[monthKey][tipoOS]) {
        monthlyData[monthKey][tipoOS] = 0;
        monthlyData[monthKey][tipoOS + '_value'] = 0;
      }

      monthlyData[monthKey][tipoOS]++;
      monthlyData[monthKey][tipoOS + '_value'] += parseFloat(order.total_value || 0);
    });

    const sortedMonths = Object.keys(monthlyData).sort();
    const tipos = Array.from(tiposSet);

    const chartArrayQuantity = sortedMonths.map(key => {
      const entry = { month: monthlyData[key].month };
      tipos.forEach(tipo => {
        entry[tipo] = monthlyData[key][tipo] || 0;
      });
      return entry;
    });

    const chartArrayRevenue = sortedMonths.map(key => {
      const entry = { month: monthlyData[key].month };
      tipos.forEach(tipo => {
        entry[tipo] = monthlyData[key][tipo + '_value'] || 0;
      });
      return entry;
    });

    setChartData(chartArrayQuantity);
    setChartDataRevenue(chartArrayRevenue);
  };

  const processTechnicianChartData = (ordersData, mode) => {
    if (!ordersData || ordersData.length === 0) return [];

    const techMap = {};
    ordersData.forEach(order => {
      const tech = order.technician || 'Sem Técnico';
      if (!techMap[tech]) {
        techMap[tech] = { name: tech, quantity: 0, value: 0 };
      }
      techMap[tech].quantity++;
      techMap[tech].value += parseFloat(order.total_value || 0);
    });

    const result = Object.values(techMap).map(t => ({
      ...t,
      ticket: t.quantity > 0 ? t.value / t.quantity : 0
    }));

    result.sort((a, b) => b[mode] - a[mode]);
    return result;
  };

  const loadAllOrdersForChart = async (adjustedFilters) => {
    try {
      const ordersData = await dashboardService.getOrders(adjustedFilters, 1, 1000);
      return ordersData.results || [];
    } catch (error) {
      if (error.message.includes('Sessão expirada')) {
        return [];
      }
      return [];
    }
  };

  const loadData = async (page = 1, customSort = null) => {
    try {
      setLoading(true);

      const currentSort = customSort || sortConfig;

      const filtersWithSort = {
        ...filters,
        sortBy: currentSort.key,
        sortDirection: currentSort.direction
      };

      const [summaryData, ordersData] = await Promise.all([
        dashboardService.getSummary(filters),
        dashboardService.getOrders(filtersWithSort, page, 20)
      ]);

      setSummary(summaryData);
      setOrders(ordersData.results || []);
      setTotalPages(ordersData.total_pages || 1);
      setTotalOrders(ordersData.count || 0);
      setCurrentPage(page);

      if (filters.type.length === 0 && summaryData && summaryData.tipos_os) {
        setTiposOSList(Object.keys(summaryData.tipos_os));
      }

      const allOrders = await loadAllOrdersForChart(filters);
      setAllOrdersForChart(allOrders);
      processChartData(allOrders);

    } catch (error) {
      if (error.message.includes('Sessão expirada')) {
        return;
      }
      
      alert('Erro ao carregar dados: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters({ ...filters, ...newFilters });
    setCurrentPage(1);
  };

  const applyFilters = () => {
    loadData(1);
  };

  const loadEquipmentList = async (search = '') => {
    try {
      const data = await dashboardService.getEquipmentList(search);
      setEquipmentList(data.results || []);
    } catch (error) {
      console.error('Erro ao carregar equipamentos:', error);
    }
  };

  const validateSyncDates = () => {
    if (!syncDates.startDate || !syncDates.endDate) {
      alert('Data inicial e final são obrigatórias para sincronização');
      return false;
    }

    const startDate = new Date(syncDates.startDate);
    const endDate = new Date(syncDates.endDate);

    if (startDate > endDate) {
      alert('Data inicial deve ser anterior à data final');
      return false;
    }

    const diffDays = (endDate - startDate) / (1000 * 60 * 60 * 24);
    if (diffDays > 365) {
      alert('Período máximo permitido: 365 dias');
      return false;
    }

    return true;
  };

  const handleSync = async () => {
    if (!validateSyncDates()) {
      return;
    }

    try {
      setSyncing(true);
      setShowSyncModal(false);

      const result = await dashboardService.syncData({
        start_date: syncDates.startDate,
        end_date: syncDates.endDate
      });

      if (result && result.success) {
        let message = `Sincronização concluída!\n\n` +
          `• ${result.total || 0} ordens retornadas pela API\n` +
          `• ${result.created || 0} ordens criadas\n` +
          `• ${result.updated || 0} ordens atualizadas\n`;
        
        if (result.skipped && result.skipped > 0) {
          message += `• ${result.skipped} ordens ignoradas (data fora do período)\n`;
        }
        
        message += `\nPeríodo: ${syncDates.startDate} a ${syncDates.endDate}`;
        
        alert(message);
        
        const newFilters = {
          startDate: syncDates.startDate,
          endDate: syncDates.endDate,
          status: [],
          equipment: [],
          technician: '',
          type: [],
          search: ''
        };
        
        setFilters(newFilters);
        setCurrentPage(1);
        loadData(1);
      } else {
        alert('Erro: ' + (result?.message || 'Erro desconhecido'));
      }
    } catch (error) {
      if (error.message.includes('Sessão expirada')) {
        return;
      }
      
      alert('Erro: ' + error.message);
    } finally {
      setSyncing(false);
    }
  };

  const handlePageChange = (page) => {
    loadData(page);
  };

  const openSyncModal = () => {
    const endDate = new Date().toISOString().split('T')[0];
    const startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    setSyncDates({
      startDate,
      endDate
    });
    setShowSyncModal(true);
  };

  const exportToXLSX = () => {
    try {
      const sortedOrders = [...allOrdersForChart].sort((a, b) => {
        return new Date(b.open_date) - new Date(a.open_date);
      });

      const exportData = sortedOrders.map(order => ({
        'Número': order.number,
        'Cliente': order.client_name,
        'Equipamento': order.equipment,
        'Tipo de OS': order.type || '-',
        'Status': order.status_display || order.status,
        'Valor': `R$ ${parseFloat(order.total_value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        'Data Abertura': (() => {
          const [year, month, day] = order.open_date.split('-');
          const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          return date.toLocaleDateString('pt-BR');
        })(),
        'Técnico': order.technician || '-'
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);

      const columnWidths = [
        { wch: 10 },
        { wch: 30 },
        { wch: 30 },
        { wch: 35 },
        { wch: 12 },
        { wch: 15 },
        { wch: 15 },
        { wch: 20 }
      ];
      ws['!cols'] = columnWidths;

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Ordens de Serviço');

      const summaryData = [];
      if (summary?.tipos_os) {
        Object.entries(summary.tipos_os).forEach(([tipoNome, tipoData]) => {
          summaryData.push({
            'Tipo de OS': tipoNome,
            'Quantidade': tipoData.count || 0,
            'Valor Total': `R$ ${parseFloat(tipoData.value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          });
        });
      }
      summaryData.push({
        'Tipo de OS': 'TOTAL',
        'Quantidade': summary?.total || 0,
        'Valor Total': `R$ ${parseFloat(summary?.total_value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      });
      const wsSummary = XLSX.utils.json_to_sheet(summaryData);
      wsSummary['!cols'] = [{ wch: 35 }, { wch: 12 }, { wch: 18 }];
      XLSX.utils.book_append_sheet(wb, wsSummary, 'Resumo');

      const fileName = `Ordens_servico_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);

      setShowExportMenu(false);
    } catch (error) {
      alert('Erro ao exportar arquivo XLSX: ' + error.message);
    }
  };

  const exportToPDF = () => {
    try {
      const doc = new jsPDF('landscape');

      doc.setFontSize(18);
      doc.text('Relatório de Ordens de Serviço', 14, 20);

      doc.setFontSize(10);
      doc.text(`Período: ${filters.startDate} a ${filters.endDate}`, 14, 28);
      doc.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, 14, 34);

      doc.setFontSize(14);
      doc.text('Resumo por Tipo de OS', 14, 45);

      const pdfSummaryBody = [];
      if (summary?.tipos_os) {
        Object.entries(summary.tipos_os).forEach(([tipoNome, tipoData]) => {
          pdfSummaryBody.push([tipoNome, tipoData.count || 0, formatCurrencyFull(tipoData.value || 0)]);
        });
      }
      pdfSummaryBody.push(['TOTAL', summary?.total || 0, formatCurrencyFull(summary?.total_value || 0)]);

      autoTable(doc, {
        startY: 50,
        head: [['Tipo de OS', 'Quantidade', 'Valor Total']],
        body: pdfSummaryBody,
        theme: 'grid',
        styles: { fontSize: 9 },
        headStyles: { fillColor: [59, 130, 246] }
      });

      doc.setFontSize(14);
      const finalY = doc.lastAutoTable.finalY || 50;
      doc.text('Listagem de Ordens', 14, finalY + 15);

      const sortedOrdersPDF = [...allOrdersForChart].sort((a, b) => {
        return new Date(b.open_date) - new Date(a.open_date);
      });

      const tableData = sortedOrdersPDF.map(order => {
        const [year, month, day] = order.open_date.split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        return [
          order.number,
          order.client_name,
          order.equipment,
          order.type || '-',
          order.status_display || order.status,
          formatCurrencyFull(order.total_value),
          date.toLocaleDateString('pt-BR'),
          order.technician
        ];
      });

      autoTable(doc, {
        startY: finalY + 20,
        head: [['Número', 'Cliente', 'Equipamento', 'Tipo de OS', 'Status', 'Valor', 'Data', 'Técnico']],
        body: tableData,
        theme: 'striped',
        styles: { fontSize: 7 },
        headStyles: { fillColor: [59, 130, 246] },
        columnStyles: {
          0: { cellWidth: 'auto' },
          1: { cellWidth: 'auto' },
          2: { cellWidth: 'auto' },
          3: { cellWidth: 'auto' },
          4: { cellWidth: 'auto' },
          5: { cellWidth: 25 },
          6: { cellWidth: 'auto' },
          7: { cellWidth: 'auto' }
        },
        margin: { left: 14, right: 14 }
      });

      const fileName = `Ordens_servico_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);

      setShowExportMenu(false);
    } catch (error) {
      alert('Erro ao exportar arquivo PDF: ' + error.message);
    }
  };

  const exportToPDFByTechnician = () => {
    try {
      const doc = new jsPDF('landscape');

      doc.setFontSize(18);
      doc.text('Relatório de OS por Técnico', 14, 20);

      doc.setFontSize(10);
      doc.text(`Período: ${filters.startDate} a ${filters.endDate}`, 14, 28);
      doc.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, 14, 34);

      // Agrupar ordens por técnico
      const ordersByTechnician = {};
      allOrdersForChart.forEach(order => {
        const techName = order.technician || 'Sem Técnico';
        if (!ordersByTechnician[techName]) {
          ordersByTechnician[techName] = {
            orders: [],
            totalValue: 0,
            count: 0
          };
        }
        ordersByTechnician[techName].orders.push(order);
        ordersByTechnician[techName].totalValue += parseFloat(order.total_value || 0);
        ordersByTechnician[techName].count++;
      });

      // Ordenar técnicos por valor total (maior para menor)
      const sortedTechnicians = Object.entries(ordersByTechnician).sort((a, b) => b[1].totalValue - a[1].totalValue);

      // Tabela resumo por técnico
      doc.setFontSize(14);
      doc.text('Totalizador por Técnico', 14, 45);

      const summaryData = sortedTechnicians.map(([techName, data]) => [
        techName,
        data.count,
        formatCurrencyFull(data.totalValue)
      ]);

      // Adicionar linha de total
      const totalOrders = sortedTechnicians.reduce((sum, [, data]) => sum + data.count, 0);
      const totalValue = sortedTechnicians.reduce((sum, [, data]) => sum + data.totalValue, 0);
      summaryData.push(['TOTAL GERAL', totalOrders, formatCurrencyFull(totalValue)]);

      autoTable(doc, {
        startY: 50,
        head: [['Técnico', 'Qtd. OS', 'Valor Total']],
        body: summaryData,
        theme: 'grid',
        styles: { fontSize: 9 },
        headStyles: { fillColor: [34, 197, 94] },
        footStyles: { fillColor: [229, 231, 235], fontStyle: 'bold' }
      });

      let currentY = doc.lastAutoTable.finalY + 15;

      // Detalhamento por técnico
      sortedTechnicians.forEach(([techName, data]) => {
        // Verificar se precisa de nova página
        if (currentY > 250) {
          doc.addPage();
          currentY = 20;
        }

        doc.setFontSize(12);
        doc.setTextColor(34, 197, 94);
        doc.text(`Técnico: ${techName} (${data.count} OS - ${formatCurrencyFull(data.totalValue)})`, 14, currentY);
        doc.setTextColor(0, 0, 0);

        const techOrders = data.orders.sort((a, b) => new Date(b.open_date) - new Date(a.open_date));

        const techTableData = techOrders.map(order => {
          const [year, month, day] = order.open_date.split('-');
          const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          return [
            order.number,
            order.client_name?.substring(0, 25) || '-',
            order.type || '-',
            order.status_display || order.status,
            formatCurrencyFull(order.total_value),
            date.toLocaleDateString('pt-BR')
          ];
        });

        autoTable(doc, {
          startY: currentY + 5,
          head: [['Nº', 'Cliente', 'Tipo', 'Status', 'Valor', 'Data']],
          body: techTableData,
          theme: 'striped',
          styles: { fontSize: 7 },
          headStyles: { fillColor: [59, 130, 246] },
          margin: { left: 14, right: 14 }
        });

        currentY = doc.lastAutoTable.finalY + 10;
      });

      const fileName = `Ordens_por_tecnico_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);

      setShowExportMenu(false);
    } catch (error) {
      alert('Erro ao exportar relatório por técnico: ' + error.message);
    }
  };

  useEffect(() => {
    loadData();
    loadEquipmentList();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportMenu && !event.target.closest('button')) {
        setShowExportMenu(false);
      }
      if (openDropdown && !event.target.closest('[data-dropdown]')) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu, openDropdown]);

  if (loading && orders.length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '400px',
        gap: '1rem'
      }}>
        <div style={{ 
          width: '40px', 
          height: '40px', 
          border: '4px solid #e5e7eb', 
          borderTop: '4px solid #3b82f6',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#6b7280', margin: 0 }}>Carregando ordens...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem'
      }}>
        <div>
          <h1 style={{ color: '#1f2937', margin: 0, fontSize: '2rem', fontWeight: '700' }}>
            Ordens de Serviço
          </h1>
          <p style={{ color: '#6b7280', margin: '0.5rem 0 0 0', fontSize: '0.875rem' }}>
            Gestão completa com sincronização API M8
          </p>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem' }}>
          {/* Botão Exportar */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              disabled={allOrdersForChart.length === 0}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: allOrdersForChart.length === 0 ? '#9ca3af' : '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: allOrdersForChart.length === 0 ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)'
              }}
            >
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Exportar Relatório
              <svg width="12" height="12" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>

            {/* Dropdown Menu */}
            {showExportMenu && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '0.5rem',
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                border: '1px solid #e5e7eb',
                zIndex: 1000,
                minWidth: '200px',
                overflow: 'hidden'
              }}>
                <button
                  onClick={exportToXLSX}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    backgroundColor: 'transparent',
                    border: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <svg width="20" height="20" fill="#10b981" viewBox="0 0 24 24">
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                  </svg>
                  <div>
                    <div style={{ fontWeight: '600', color: '#1f2937' }}>Exportar XLSX</div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Planilha Excel</div>
                  </div>
                </button>

                <div style={{ height: '1px', backgroundColor: '#e5e7eb', margin: '0 0.5rem' }}></div>

                <button
                  onClick={exportToPDF}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    backgroundColor: 'transparent',
                    border: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <svg width="20" height="20" fill="#dc2626" viewBox="0 0 24 24">
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                  </svg>
                  <div>
                    <div style={{ fontWeight: '600', color: '#1f2937' }}>Exportar PDF</div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Documento PDF geral</div>
                  </div>
                </button>

                <div style={{ height: '1px', backgroundColor: '#e5e7eb', margin: '0 0.5rem' }}></div>

                <button
                  onClick={exportToPDFByTechnician}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    backgroundColor: 'transparent',
                    border: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <svg width="20" height="20" fill="#22c55e" viewBox="0 0 24 24">
                    <path d="M12,5.5A3.5,3.5 0 0,1 15.5,9A3.5,3.5 0 0,1 12,12.5A3.5,3.5 0 0,1 8.5,9A3.5,3.5 0 0,1 12,5.5M5,8C5.56,8 6.08,8.15 6.53,8.42C6.38,9.85 6.8,11.27 7.66,12.38C7.16,13.34 6.16,14 5,14A3,3 0 0,1 2,11A3,3 0 0,1 5,8M19,8A3,3 0 0,1 22,11A3,3 0 0,1 19,14C17.84,14 16.84,13.34 16.34,12.38C17.2,11.27 17.62,9.85 17.47,8.42C17.92,8.15 18.44,8 19,8M5.5,18.25C5.5,16.18 8.41,14.5 12,14.5C15.59,14.5 18.5,16.18 18.5,18.25V20H5.5V18.25M0,20V18.5C0,17.11 1.89,15.94 4.45,15.6C3.86,16.28 3.5,17.22 3.5,18.25V20H0M24,20H20.5V18.25C20.5,17.22 20.14,16.28 19.55,15.6C22.11,15.94 24,17.11 24,18.5V20Z" />
                  </svg>
                  <div>
                    <div style={{ fontWeight: '600', color: '#1f2937' }}>PDF por Técnico</div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Totalizador de OS por técnico</div>
                  </div>
                </button>
              </div>
            )}
          </div>

          {/* Botão Sincronizar */}
          <button
            onClick={openSyncModal}
            disabled={syncing}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: syncing ? '#9ca3af' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: syncing ? 'not-allowed' : 'pointer',
              fontSize: '0.875rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)'
            }}
          >
            {syncing ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid #ffffff40',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                Sincronizando...
              </>
            ) : (
              <>
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Sincronizar Ordens
              </>
            )}
          </button>
        </div>
      </div>

      {!loading && orders.length === 0 && (
        <div style={{
          backgroundColor: '#f0f9ff',
          border: '1px solid #0ea5e9',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '2rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <svg width="24" height="24" fill="currentColor" style={{ color: '#0ea5e9', minWidth: '24px' }} viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div>
            <h3 style={{ margin: '0 0 0.5rem 0', fontWeight: '600', color: '#0c4a6e', fontSize: '1.125rem' }}>
              Nenhuma ordem encontrada
            </h3>
            <p style={{ margin: 0, fontSize: '0.875rem', color: '#0c4a6e', lineHeight: '1.5' }}>
              Use "Sincronizar Ordens" para importar dados da API M8
            </p>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div style={{
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
        border: '1px solid #f1f5f9',
        marginBottom: '2rem'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '1.5rem',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.25rem', fontWeight: '700' }}>
            Filtros de Visualização
          </h3>
        </div>

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '1.5rem',
          marginBottom: '2rem',
          alignItems: 'flex-end'
        }}>
          <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
              Data Abertura - Início
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange({ startDate: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            />
          </div>
          <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
              Data Abertura - Fim
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange({ endDate: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            />
          </div>
          <div data-dropdown style={{ flex: '1 1 200px', minWidth: '200px', position: 'relative' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
              Status
            </label>
            <div
              onClick={() => setOpenDropdown(openDropdown === 'status' ? null : 'status')}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: `1px solid ${openDropdown === 'status' ? '#3b82f6' : '#d1d5db'}`,
                borderRadius: '6px',
                fontSize: '0.875rem',
                backgroundColor: 'white',
                cursor: 'pointer',
                boxSizing: 'border-box',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                minHeight: '42px'
              }}
            >
              <span style={{ color: filters.status.length === 0 ? '#9ca3af' : '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {filters.status.length === 0 ? 'Todos os Status' : filters.status.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(', ')}
              </span>
              <svg width="12" height="12" fill="#6b7280" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
            {openDropdown === 'status' && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                marginTop: '4px',
                backgroundColor: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                zIndex: 100,
                maxHeight: '200px',
                overflowY: 'auto'
              }}>
                {[
                  { value: 'pendente', label: 'Pendente' },
                  { value: 'aberto', label: 'Aberto' },
                  { value: 'cancelado', label: 'Cancelado' },
                  { value: 'faturado', label: 'Faturado' }
                ].map(option => (
                  <label key={option.value} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 0.75rem',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    color: '#374151',
                    transition: 'background-color 0.15s'
                  }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <input
                      type="checkbox"
                      checked={filters.status.includes(option.value)}
                      onChange={() => {
                        const newStatus = filters.status.includes(option.value)
                          ? filters.status.filter(s => s !== option.value)
                          : [...filters.status, option.value];
                        handleFilterChange({ status: newStatus });
                      }}
                      style={{ accentColor: '#3b82f6' }}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            )}
          </div>
          <div data-dropdown style={{ flex: '1 1 200px', minWidth: '200px', position: 'relative' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
              Tipo de Ordem de Serviço
            </label>
            <div
              onClick={() => setOpenDropdown(openDropdown === 'type' ? null : 'type')}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: `1px solid ${openDropdown === 'type' ? '#3b82f6' : '#d1d5db'}`,
                borderRadius: '6px',
                fontSize: '0.875rem',
                backgroundColor: 'white',
                cursor: 'pointer',
                boxSizing: 'border-box',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                minHeight: '42px'
              }}
            >
              <span style={{ color: filters.type.length === 0 ? '#9ca3af' : '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {filters.type.length === 0 ? 'Todos os Tipos' : filters.type.join(', ')}
              </span>
              <svg width="12" height="12" fill="#6b7280" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
            {openDropdown === 'type' && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                marginTop: '4px',
                backgroundColor: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                zIndex: 100,
                maxHeight: '200px',
                overflowY: 'auto'
              }}>
                {tiposOSList.map(tipo => (
                  <label key={tipo} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 0.75rem',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    color: '#374151',
                    transition: 'background-color 0.15s'
                  }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <input
                      type="checkbox"
                      checked={filters.type.includes(tipo)}
                      onChange={() => {
                        const newType = filters.type.includes(tipo)
                          ? filters.type.filter(t => t !== tipo)
                          : [...filters.type, tipo];
                        handleFilterChange({ type: newType });
                      }}
                      style={{ accentColor: '#3b82f6' }}
                    />
                    {tipo}
                  </label>
                ))}
              </div>
            )}
          </div>
          <div data-dropdown style={{ flex: '1 1 200px', minWidth: '200px', position: 'relative' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
              Equipamento
            </label>
            <div
              onClick={() => {
                if (openDropdown !== 'equipment') {
                  setOpenDropdown('equipment');
                  setEquipmentSearch('');
                  loadEquipmentList();
                } else {
                  setOpenDropdown(null);
                }
              }}
              style={{
                width: '100%',
                padding: filters.equipment.length > 0 ? '0.375rem' : '0.75rem',
                border: `1px solid ${openDropdown === 'equipment' ? '#3b82f6' : '#d1d5db'}`,
                borderRadius: '6px',
                fontSize: '0.875rem',
                backgroundColor: 'white',
                cursor: 'pointer',
                boxSizing: 'border-box',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                minHeight: '42px',
                gap: '0.25rem',
                flexWrap: 'wrap'
              }}
            >
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', flex: 1 }}>
                {filters.equipment.length === 0 ? (
                  <span style={{ color: '#9ca3af', padding: '0.125rem 0.375rem' }}>Todos os Equipamentos</span>
                ) : (
                  filters.equipment.map(equip => (
                    <span
                      key={equip}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        backgroundColor: '#eff6ff',
                        color: '#1e40af',
                        padding: '0.2rem 0.5rem',
                        borderRadius: '4px',
                        fontSize: '0.8rem',
                        fontWeight: '500',
                        maxWidth: '160px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{equip}</span>
                      <svg
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFilterChange({ equipment: filters.equipment.filter(eq => eq !== equip) });
                        }}
                        width="14" height="14" fill="none" stroke="#3b82f6" viewBox="0 0 24 24"
                        style={{ cursor: 'pointer', flexShrink: 0 }}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </span>
                  ))
                )}
              </div>
              <svg width="12" height="12" fill="#6b7280" viewBox="0 0 20 20" style={{ flexShrink: 0 }}>
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
            {openDropdown === 'equipment' && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                marginTop: '4px',
                backgroundColor: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                zIndex: 100,
                maxHeight: '280px',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <div style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>
                  <input
                    type="text"
                    placeholder="Buscar equipamento..."
                    value={equipmentSearch}
                    onChange={(e) => {
                      setEquipmentSearch(e.target.value);
                      loadEquipmentList(e.target.value);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '4px',
                      fontSize: '0.825rem',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
                <div style={{ overflowY: 'auto', maxHeight: '220px' }}>
                  {equipmentList.map(equip => (
                    <label key={equip} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem 0.75rem',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      color: '#374151',
                      transition: 'background-color 0.15s'
                    }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <input
                        type="checkbox"
                        checked={filters.equipment.includes(equip)}
                        onChange={() => {
                          const newEquipment = filters.equipment.includes(equip)
                            ? filters.equipment.filter(e => e !== equip)
                            : [...filters.equipment, equip];
                          handleFilterChange({ equipment: newEquipment });
                        }}
                        onClick={(e) => e.stopPropagation()}
                        style={{ accentColor: '#3b82f6' }}
                      />
                      {equip}
                    </label>
                  ))}
                  {equipmentList.length === 0 && equipmentSearch && (
                    <div style={{ padding: '0.75rem', fontSize: '0.825rem', color: '#9ca3af', textAlign: 'center' }}>
                      Nenhum equipamento encontrado
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
              Técnico
            </label>
            <input
              type="text"
              placeholder="Nome..."
              value={filters.technician}
              onChange={(e) => handleFilterChange({ technician: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            />
          </div>
          <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
              Busca
            </label>
            <input
              type="text"
              placeholder="Pesquisar..."
              value={filters.search}
              onChange={(e) => handleFilterChange({ search: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            />
          </div>
        </div>

        <div style={{ 
          display: 'flex', 
          gap: '1rem', 
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
          borderTop: '1px solid #f1f5f9',
          paddingTop: '1.5rem'
        }}>
           <button
            onClick={() => {
              setFilters({
                startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                endDate: new Date().toISOString().split('T')[0],
                status: [],
                equipment: [],
                technician: '',
                type: [],
                search: ''
              });
              setCurrentPage(1);
              applyFilters();
            }}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: 'white',
              color: '#4b5563',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '600',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#f9fafb';
              e.currentTarget.style.borderColor = '#9ca3af';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
              e.currentTarget.style.borderColor = '#d1d5db';
            }}
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Limpar
          </button>
          
          <button
            onClick={applyFilters}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '600',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#059669';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#10b981';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Aplicar Filtros
          </button>
        </div>
      </div>

      {/* Cards por Tipo de OS */}
      {summary && summary.tipos_os && Object.keys(summary.tipos_os).length > 0 && (
        <>
          <h3 style={{ margin: '0 0 1rem 0', color: '#1f2937', fontSize: '1.125rem', fontWeight: '600' }}>
            Resumo por Tipo de OS
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '1.5rem',
            marginBottom: '2rem'
          }}>
            {Object.entries(summary.tipos_os).map(([tipoNome, tipoData]) => {
              const tipoIndex = tiposOSList.indexOf(tipoNome);
              const colorIndex = tipoIndex >= 0 ? tipoIndex : Object.keys(summary.tipos_os).indexOf(tipoNome);
              const colorSet = TIPO_OS_COLORS[colorIndex % TIPO_OS_COLORS.length];

              return (
                <div key={tipoNome} style={{
                  backgroundColor: 'white',
                  padding: '1.5rem',
                  borderRadius: '12px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
                  border: '1px solid #f1f5f9',
                  transition: 'transform 0.2s ease'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                    <h3 style={{ margin: 0, color: colorSet.textColor, fontSize: '0.875rem', fontWeight: '600' }}>
                      {tipoNome}
                    </h3>
                    <div style={{
                      width: '12px',
                      height: '12px',
                      backgroundColor: colorSet.color,
                      borderRadius: '50%'
                    }}></div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                    <div>
                      <p style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0, color: '#1f2937', lineHeight: '1' }}>
                        R$ {parseFloat(tipoData.value || 0).toLocaleString('pt-BR', {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0
                        })}
                      </p>
                      <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.25rem 0 0 0' }}>valor total</p>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0, color: '#FFC000' }}>
                        R$ {tipoData.count > 0
                          ? (parseFloat(tipoData.value || 0) / tipoData.count).toLocaleString('pt-BR', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })
                          : '0,00'}
                      </p>
                      <p style={{ fontSize: '0.75rem', color: '#FFC000', margin: '0.25rem 0 0 0' }}>ticket médio</p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0, color: colorSet.textColor }}>
                        {tipoData.count || 0}
                      </p>
                      <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.25rem 0 0 0' }}>ordens</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Gráficos - Quantidade e Faturamento por Tipo de OS */}
      {chartData.length > 0 && chartDataRevenue.length > 0 && (() => {
        const tiposOS = Object.keys(chartData[0]).filter(k => k !== 'month');
        const getChartColor = (tipo) => {
          const tipoIndex = tiposOSList.indexOf(tipo);
          const index = tipoIndex >= 0 ? tipoIndex : tiposOS.indexOf(tipo);
          return TIPO_OS_COLORS[index % TIPO_OS_COLORS.length].color;
        };
        return (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          {/* Gráfico de Quantidade (Esquerda) */}
          <div style={{
            backgroundColor: 'white',
            padding: '1.5rem',
            borderRadius: '12px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
            border: '1px solid #f1f5f9'
          }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#1f2937', fontSize: '1.125rem', fontWeight: '600' }}>
              Quantidade por Tipo de OS
            </h3>
            <p style={{ margin: '0 0 1.5rem 0', fontSize: '0.8rem', color: '#6b7280' }}>
              Total: {allOrdersForChart.length} ordens em {chartData.length} {chartData.length === 1 ? 'mês' : 'meses'}
            </p>
            <div style={{ width: '100%', height: '350px' }}>
              <ResponsiveContainer>
                <BarChart
                  data={chartData}
                  margin={{ top: 30, right: 10, left: 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    tickLine={{ stroke: '#d1d5db' }}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    tickLine={{ stroke: '#d1d5db' }}
                    domain={[0, 'dataMax + 5']}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                  <Legend wrapperStyle={{ paddingTop: '15px', fontSize: '12px' }} />
                  {tiposOS.map((tipo) => (
                    <Bar key={tipo} dataKey={tipo} fill={getChartColor(tipo)} name={tipo} radius={[2, 2, 0, 0]}>
                      <LabelList dataKey={tipo} position="top" style={{fontSize: '11px', fill: '#6b7280'}} />
                    </Bar>
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Gráfico de Faturamento (Direita) */}
          <div style={{
            backgroundColor: 'white',
            padding: '1.5rem',
            borderRadius: '12px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
            border: '1px solid #f1f5f9'
          }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#1f2937', fontSize: '1.125rem', fontWeight: '600' }}>
              Faturamento por Tipo de OS
            </h3>
            <p style={{ margin: '0 0 1.5rem 0', fontSize: '0.8rem', color: '#6b7280' }}>
              Valores em Real (R$)
            </p>
            <div style={{ width: '100%', height: '350px' }}>
              <ResponsiveContainer>
                <BarChart
                  data={chartDataRevenue}
                  margin={{ top: 30, right: 10, left: 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    tickLine={{ stroke: '#d1d5db' }}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    tickLine={{ stroke: '#d1d5db' }}
                    tickFormatter={formatCurrency}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}
                    formatter={(value) => formatCurrencyFull(value)}
                  />
                  <Legend wrapperStyle={{ paddingTop: '15px', fontSize: '12px' }} />
                  {tiposOS.map((tipo) => (
                    <Bar key={tipo} dataKey={tipo} fill={getChartColor(tipo)} name={tipo} radius={[2, 2, 0, 0]}>
                      <LabelList dataKey={tipo} position="top" formatter={formatCurrency} style={{fontSize: '10px', fill: '#6b7280'}} />
                    </Bar>
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Gráfico por Técnico */}
      {allOrdersForChart.length > 0 && (() => {
        const techData = processTechnicianChartData(allOrdersForChart, technicianChartMode);
        if (techData.length === 0) return null;

        const modeConfig = {
          quantity: { dataKey: 'quantity', label: 'Quantidade de OS', formatter: (v) => v, color: '#3b82f6' },
          value: { dataKey: 'value', label: 'Valor Total', formatter: formatCurrencyFull, color: '#10b981' },
          ticket: { dataKey: 'ticket', label: 'Ticket Médio', formatter: formatCurrencyFull, color: '#f59e0b' }
        };
        const config = modeConfig[technicianChartMode];
        const chartHeight = Math.max(300, techData.length * 45);

        return (
          <div style={{
            backgroundColor: 'white',
            padding: '1.5rem',
            borderRadius: '12px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
            border: '1px solid #f1f5f9',
            marginBottom: '2rem'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
              <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.125rem', fontWeight: '600' }}>
                Desempenho por Técnico
              </h3>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {[
                  { key: 'quantity', label: 'Quantidade de OS' },
                  { key: 'value', label: 'Valor Total' },
                  { key: 'ticket', label: 'Ticket Médio' }
                ].map(btn => (
                  <button
                    key={btn.key}
                    onClick={() => setTechnicianChartMode(btn.key)}
                    style={{
                      padding: '0.5rem 1rem',
                      fontSize: '0.8rem',
                      fontWeight: '600',
                      borderRadius: '6px',
                      border: technicianChartMode === btn.key ? 'none' : '1px solid #d1d5db',
                      backgroundColor: technicianChartMode === btn.key ? modeConfig[btn.key].color : 'white',
                      color: technicianChartMode === btn.key ? 'white' : '#4b5563',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    {btn.label}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ width: '100%', height: `${chartHeight}px` }}>
              <ResponsiveContainer>
                <BarChart
                  data={techData}
                  layout="vertical"
                  margin={{ top: 5, right: 80, left: 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    tickFormatter={technicianChartMode !== 'quantity' ? formatCurrency : undefined}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={150}
                    tick={{ fontSize: 11, fill: '#374151' }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}
                    formatter={(value) => [config.formatter(value), config.label]}
                  />
                  <Bar dataKey={config.dataKey} fill={config.color} radius={[0, 4, 4, 0]} name={config.label}>
                    <LabelList
                      dataKey={config.dataKey}
                      position="right"
                      formatter={config.formatter}
                      style={{ fontSize: '11px', fill: '#374151', fontWeight: '600' }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        );
      })()}

      {/* Tabela */}
      <div style={{ 
        backgroundColor: 'white', 
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
        border: '1px solid #f1f5f9',
        overflow: 'hidden'
      }}>
        <div style={{ 
          padding: '1.5rem', 
          borderBottom: '1px solid #f1f5f9', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          backgroundColor: '#fafbfc'
        }}>
          <h2 style={{ margin: 0, color: '#1f2937', fontSize: '1.25rem', fontWeight: '700' }}>
            Listagem de Ordens
          </h2>
          <span style={{ 
            color: '#6b7280', 
            fontSize: '0.875rem',
            backgroundColor: '#f3f4f6',
            padding: '0.5rem 1rem',
            borderRadius: '20px',
            fontWeight: '500'
          }}>
            Total: {totalOrders} | Página {currentPage} de {totalPages}
          </span>
        </div>
        
        {loading ? (
          <div style={{ padding: '3rem', textAlign: 'center' }}>
            <div style={{ 
              width: '32px', 
              height: '32px', 
              border: '3px solid #e5e7eb', 
              borderTop: '3px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1rem'
            }}></div>
            <p style={{ color: '#6b7280', margin: 0 }}>Carregando...</p>
          </div>
        ) : orders.length > 0 ? (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: '#f8fafc' }}>
                  <tr>
                    <SortableHeader label="Número" sortKey="number" style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#4b5563', borderBottom: '2px solid #e5e7eb' }} />
                    <SortableHeader label="Cliente" sortKey="client_name" style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#4b5563', borderBottom: '2px solid #e5e7eb' }} />
                    <SortableHeader label="Equipamento" sortKey="equipment" style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#4b5563', borderBottom: '2px solid #e5e7eb' }} />
                    <SortableHeader label="Tipo" sortKey="type" style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#4b5563', borderBottom: '2px solid #e5e7eb' }} />
                    <SortableHeader label="Status" sortKey="status" style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#4b5563', borderBottom: '2px solid #e5e7eb' }} />
                    <SortableHeader label="Valor" sortKey="total_value" style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#4b5563', borderBottom: '2px solid #e5e7eb' }} />
                    <SortableHeader label="Data" sortKey="open_date" style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#4b5563', borderBottom: '2px solid #e5e7eb' }} />
                    <SortableHeader label="Técnico" sortKey="technician" style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#4b5563', borderBottom: '2px solid #e5e7eb' }} />
                  </tr>
                </thead>
                <tbody>
                  {getSortedOrders().map((order) => {
                    const statusColors = getStatusColor(order.status);
                    return (
                      <tr key={order.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', fontWeight: '500', color: '#1f2937' }}>
                          {order.number}
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#374151' }}>
                          {order.client_name}
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#374151' }}>
                          {order.equipment}
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.75rem', color: '#6b7280' }}>
                          {order.type || '-'}
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                          <span style={{
                            padding: '0.375rem 0.75rem',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            backgroundColor: statusColors.bgColor,
                            color: statusColors.textColor
                          }}>
                            {order.status_display || order.status}
                          </span>
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#374151', fontWeight: '500' }}>
                          {formatCurrencyFull(order.total_value)}
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#374151' }}>
                          {(() => {
                            const [year, month, day] = order.open_date.split('-');
                            const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                            return date.toLocaleDateString('pt-BR');
                          })()}
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#374151' }}>
                          {order.technician || '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div style={{ 
                padding: '1.5rem', 
                borderTop: '1px solid #f1f5f9', 
                display: 'flex', 
                justifyContent: 'center', 
                gap: '0.75rem',
                backgroundColor: '#fafbfc'
              }}>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  style={{
                    padding: '0.75rem 1rem',
                    border: '2px solid #e5e7eb',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    opacity: currentPage === 1 ? 0.5 : 1,
                    fontWeight: '500',
                    fontSize: '0.875rem'
                  }}
                >
                  ← Anterior
                </button>

                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={i}
                      onClick={() => handlePageChange(pageNum)}
                      style={{
                        padding: '0.75rem 1rem',
                        border: `2px solid ${pageNum === currentPage ? '#3b82f6' : '#e5e7eb'}`,
                        backgroundColor: pageNum === currentPage ? '#3b82f6' : 'white',
                        color: pageNum === currentPage ? 'white' : '#374151',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '500',
                        fontSize: '0.875rem',
                        minWidth: '44px'
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '0.75rem 1rem',
                    border: '2px solid #e5e7eb',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    opacity: currentPage === totalPages ? 0.5 : 1,
                    fontWeight: '500',
                    fontSize: '0.875rem'
                  }}
                >
                  Próxima →
                </button>
              </div>
            )}
          </>
        ) : (
          <div style={{ padding: '4rem', textAlign: 'center', color: '#6b7280' }}>
            <svg style={{ width: '64px', height: '64px', margin: '0 auto 1.5rem', color: '#d1d5db' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem', color: '#374151' }}>
              Nenhuma ordem encontrada
            </h3>
            <p style={{ margin: 0 }}>
              Ajuste os filtros ou sincronize novos dados
            </p>
          </div>
        )}
      </div>

      {/* Modal */}
      {showSyncModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '16px',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
            width: '90%',
            maxWidth: '500px'
          }}>
            <h3 style={{ margin: '0 0 1.5rem 0', color: '#1f2937', fontSize: '1.5rem', fontWeight: '700' }}>
              Sincronizar Ordens
            </h3>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600' }}>
                Data Inicial:
              </label>
              <input
                type="date"
                value={syncDates.startDate}
                onChange={(e) => setSyncDates({...syncDates, startDate: e.target.value})}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '0.875rem'
                }}
              />
            </div>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600' }}>
                Data Final:
              </label>
              <input
                type="date"
                value={syncDates.endDate}
                onChange={(e) => setSyncDates({...syncDates, endDate: e.target.value})}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '0.875rem'
                }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowSyncModal(false)}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '2px solid #e5e7eb',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleSync}
                disabled={!syncDates.startDate || !syncDates.endDate}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: (!syncDates.startDate || !syncDates.endDate) ? '#9ca3af' : '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: (!syncDates.startDate || !syncDates.endDate) ? 'not-allowed' : 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '600'
                }}
              >
                Sincronizar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceOrders;